import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import DefaultDialog from "../DefaultDialog";
import { Stack, Typography } from "@mui/material";
import { getAppDistributionIdentifier } from "@/utils";
import { AppDistributionIdentifier } from "@/store";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { useTranslation } from "react-i18next";
import ButtonWithAnalytics from "../ButtonWithAnalytics";

interface AppUpdateHandlerProps {
  children: React.ReactNode;
}

export default function AppUpdateHandler({ children }: AppUpdateHandlerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [isAppUpdateModalOpen, setIsAppUpdateModalOpen] = useState(false);
  const isForceAppUpdateEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_FORCE_APP_UPDATE
  );
  const appleStoreLink = import.meta.env.VITE_APP_UNU_HEALTH_APPLE_STORE_URI;
  const googlePlayStoreLink = import.meta.env
    .VITE_APP_UNU_HEALTH_GOOGLE_PLAY_STORE_URI;
  const huaweiStoreLink = import.meta.env.VITE_APP_UNU_HEALTH_HUAWEI_STORE_URI;

  const [appStoreLink, setAppStoreLink] = useState<string | undefined>(
    googlePlayStoreLink
  );

  useEffect(() => {
    if (location.hash.includes("distribution")) {
      const distribution = getAppDistributionIdentifier(location.hash);
      const isInWebView = window.ReactNativeWebView !== undefined;
      if (
        isForceAppUpdateEnabled &&
        !isInWebView &&
        distribution !== AppDistributionIdentifier.WEB &&
        distribution !== AppDistributionIdentifier.PWA
      ) {
        setIsAppUpdateModalOpen(true);
      }
      if (
        distribution === AppDistributionIdentifier.APPLE_APP_STORE &&
        appleStoreLink
      ) {
        setAppStoreLink(appleStoreLink);
      } else if (
        distribution === AppDistributionIdentifier.HUAWEI_APP_GALLERY
      ) {
        setAppStoreLink(huaweiStoreLink);
      } else {
        setAppStoreLink(googlePlayStoreLink);
      }
    }
  }, [isForceAppUpdateEnabled, location]);

  return (
    <>
      {children}
      <DefaultDialog
        isOpen={isAppUpdateModalOpen}
        onClose={() => setIsAppUpdateModalOpen(false)}
        closeButton={false}
      >
        <Stack spacing={2} justifyContent="center" textAlign="center">
          <Typography variant="h2">{t("AppUpdateHandler.title")}</Typography>
          <Typography variant="h5">{t("AppUpdateHandler.subtitle")}</Typography>
          <ButtonWithAnalytics
            page="Launch"
            text={t("AppUpdateHandler.button")}
            intent="navigational"
            size="small"
            href={appStoreLink}
          >
            {t("AppUpdateHandler.button")}
          </ButtonWithAnalytics>
        </Stack>
      </DefaultDialog>
    </>
  );
}
